/**
 * Guards the Global Bar product switcher while the session is acting as a
 * VAU.
 *
 * Problem: when a user is impersonating a VAU and then switches org /
 * product / instance from the Opti ID global bar, the app lands in a state
 * the user can't get out of — the new surface loads under the VAU identity
 * with no way back. The global bar has no *pre*-switch event to cancel, so
 * instead we run it with `disable-navigation="true"` (while assumed, and —
 * as a fail-safe — while the assumed-context query is still loading OR has
 * errored), which suppresses its built-in navigation and hands us the
 * `@opti-common-header:change` event to handle ourselves.
 *
 * Behaviour:
 *   - Loading (assumed-context in flight) → `disableNavigation` is true;
 *     any change event is *queued* and replayed once the query resolves, so
 *     a fast mid-load click isn't silently swallowed. This closes a
 *     cold-load race where a click could otherwise land an assumed session
 *     on another surface before we know we're assumed.
 *   - Error (assumed-context query failed) → treated as "may be assumed":
 *     `disableNavigation` stays true and a change is routed through the
 *     confirm→release path. Release is idempotent, so this is a harmless
 *     no-op if the caller turns out not to have been assumed — the trade is
 *     that we'd rather briefly block the bar for a genuinely-non-assumed
 *     user during a backend blip than leak a VAU identity across products.
 *   - Not assumed (query resolved) → `disableNavigation` is false; the bar
 *     navigates as usual and this hook does nothing (a replayed mid-load
 *     click is completed for them, since the bar was suppressed at click
 *     time).
 *   - Assumed → `disableNavigation` is true; on a selection we surface a
 *     confirmation. Confirming releases the VAU (swapping the token record
 *     back to the principal) BEFORE navigating, so the destination loads as
 *     the user, not the VAU. Cancelling stays put.
 *
 * The release goes through `useReleaseVAU` (passing the target URL so it
 * reloads onto the workspace the user picked, not the one they're leaving)
 * — this keeps the identity-bound React Query cache eviction + the
 * `REACT_QUERY_OFFLINE_CACHE` localStorage cleanup in one place, so the
 * destination surface never rehydrates stale VAU-identity data.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toaster } from '@optiaxiom/react';

import { useReleaseVAU, useVAUContext } from '@frontend/features/vau/hooks/useVAUContext';
import { useInstanceId } from '@frontend/hooks';

import {
  type GlobalBarChangeDetail,
  type GlobalBarSwitchTarget,
  resolveGlobalBarSwitchTarget,
} from './resolveGlobalBarSwitchTarget';

const CHANGE_EVENT = '@opti-common-header:change';

export type VauSwitchGuard = {
  /** Whether to pass `disable-navigation` to the global bar. */
  disableNavigation: boolean;
  /** Set when a switch is pending confirmation; drives the dialog. */
  pendingTarget: GlobalBarSwitchTarget | null;
  /** VAU display name, for the confirmation copy. */
  vauName?: string;
  /** Principal email, for the confirmation copy. */
  principalEmail?: string;
  /** True while the release call is in flight. */
  isReleasing: boolean;
  /** Release the VAU then navigate to the pending target. */
  confirm: () => void;
  /** Dismiss the pending switch and stay as the VAU. */
  cancel: () => void;
};

export function useVauSwitchGuard(headerRef: React.RefObject<HTMLElement | null>): VauSwitchGuard {
  const { data, isLoading, isError } = useVAUContext();
  const { instanceId } = useInstanceId();
  const release = useReleaseVAU();

  const isAssumed = data?.is_assumed ?? false;
  const principalEmail = data?.original_user_email;

  const [pendingTarget, setPendingTarget] = useState<GlobalBarSwitchTarget | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);
  // Holds a change event that fired before the assumed-context query
  // resolved, so we can replay it instead of dropping it (see drain effect).
  const queuedDetailRef = useRef<GlobalBarChangeDetail | null>(null);

  // Fail-safe: while the query is loading we don't yet know if the caller is
  // a VAU, and if it errored we can't confirm they aren't. In both cases keep
  // the bar in disable-navigation mode so a click can't send an assumed
  // session onto another surface before the guard can reason about it. Once
  // the query succeeds, either the confirm path takes over (assumed) or the
  // attribute is dropped and the bar navigates normally on the next click.
  const disableNavigation = isLoading || isError || isAssumed;

  // Act on a resolved change detail. When we know (assumed) or can't rule out
  // (error) a VAU session, we confirm→release→navigate. `allowDirectNav` is
  // set only for the replayed mid-load click of a *non-assumed* user, where
  // the bar was suppressed at click time and so never performed the nav.
  const handleDetail = useCallback(
    (detail: GlobalBarChangeDetail, allowDirectNav: boolean) => {
      const target = resolveGlobalBarSwitchTarget(detail, {
        currentInstanceId: instanceId,
        principalEmail,
      });

      if (isAssumed || isError) {
        if (target) {
          setPendingTarget(target);
          return;
        }
        // A product-only selection (Home / App Directory) we couldn't resolve
        // to a URL. The bar's navigation is suppressed, so without feedback
        // the click is a silent dead-end — tell the user why instead.
        if (detail?.product && !detail?.instance) {
          const name = detail.product.text || 'that product';
          toaster.create(
            `Switching to ${name} isn't available while acting as a VT — switch back to your profile first.`,
            { type: 'warning' }
          );
        }
        return;
      }

      // Not assumed and the query succeeded. Live events need no handling
      // (disable-navigation was off, so the bar already navigated). But a
      // replayed mid-load click was suppressed at click time, so complete the
      // navigation the user intended.
      if (allowDirectNav && target) {
        window.location.assign(target.url);
      }
    },
    [isAssumed, isError, instanceId, principalEmail]
  );

  const onHeaderChange = useCallback(
    (event: Event) => {
      const detail = (event as CustomEvent<GlobalBarChangeDetail>).detail;
      // Mid-load: queue the (latest) event and replay it once we know what
      // we're guarding, rather than dropping the user's click. `disable-
      // navigation` is already suppressing the bar's auto-nav in this window.
      if (isLoading) {
        queuedDetailRef.current = detail;
        return;
      }
      handleDetail(detail, false);
    },
    [isLoading, handleDetail]
  );

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    el.addEventListener(CHANGE_EVENT, onHeaderChange);
    return () => el.removeEventListener(CHANGE_EVENT, onHeaderChange);
  }, [headerRef, onHeaderChange]);

  // Drain a queued mid-load click once the assumed-context query settles.
  useEffect(() => {
    if (isLoading) return;
    const detail = queuedDetailRef.current;
    if (!detail) return;
    queuedDetailRef.current = null;
    handleDetail(detail, true);
  }, [isLoading, handleDetail]);

  const confirm = useCallback(() => {
    if (!pendingTarget) return;
    setIsReleasing(true);
    release.mutate(
      // Suppress useReleaseVAU's built-in toast — we show our own,
      // more-actionable message below so the two don't stack.
      { targetUrl: pendingTarget.url, suppressErrorToast: true },
      {
        // Success path — release + hard-reload (with cache eviction) is
        // handled inside `useReleaseVAU`; the page is about to unload, so
        // there is nothing to do here.
        onError: (err: Error) => {
          toaster.create(`Could not switch back to your profile: ${err.message}`, {
            type: 'danger',
          });
          setIsReleasing(false);
          setPendingTarget(null);
        },
      }
    );
  }, [pendingTarget, release]);

  const cancel = useCallback(() => {
    if (isReleasing) return;
    setPendingTarget(null);
  }, [isReleasing]);

  return {
    disableNavigation,
    pendingTarget,
    vauName: data?.vau_name,
    principalEmail,
    isReleasing,
    confirm,
    cancel,
  };
}
